import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { DECRYPT_PO_URL_API, UPDATE_REQUEST_RATE_API } from '../config/API';
import { MdSend, MdCheckCircle, MdError, MdCloudUpload } from 'react-icons/md';
import Swal from 'sweetalert2';
import NotFound from './NotFound';
import './VendorPortal.css';
import { decryptToken } from '../utils/secureToken';
import { VENDOR_PORTAL_URL } from '../config/API';
import { formatDate } from '../utils/dateUtils';

const VendorPortal = () => {
    const { requestId, vendorId } = useParams();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [poData, setPoData] = useState(null);
    const [items, setItems] = useState([]);
    const [grandTotal, setGrandTotal] = useState(0);
    const [currentVendor, setCurrentVendor] = useState(null);
    const [reqVendorId, setReqVendorId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Filter items based on search
    const filteredItems = items.filter(item =>
        (item.itemName && item.itemName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    useEffect(() => {
        const fetchPortalData = async () => {
            // Decrypt tokens for validation (if needed for later logic)
            decryptToken(requestId);
            decryptToken(vendorId);

            if (!requestId || !vendorId) {
                setError("Invalid or missing tokens in URL.");
                setLoading(false);
                return;
            }

            try {
                // Construct the full URL for the backend decryption API

                const fullPortalUrl = `${VENDOR_PORTAL_URL}/vendor-portal/${requestId}/${vendorId}`;

                // Call the backend API correctly using the utility constant
                const response = await axios.get(DECRYPT_PO_URL_API(encodeURIComponent(fullPortalUrl)));


                if (response.data.status) {
                    const data = response.data;

                    // Map Backend Response to State
                    const mainData = data.purchaseOrder || data.request || data.req || {};
                    setPoData({
                        id: mainData.id,
                        poNumber: mainData.poNumber || mainData.reqNumber || 'N/A',
                        projectName: data.project?.projectName || mainData.ProjectMaster?.projectName || mainData.Project?.projectName || 'N/A',
                        date: mainData.date,
                        tillDate: mainData.tillDate || mainData.validTill || mainData.date,
                        particular: mainData.particular
                    });

                    setCurrentVendor(data.currentVendor);

                    // Extract reqVendorId from vendors list
                    const vendorAssociation = (data.vendors?.list || []).find(v => v.vendorId === data.currentVendor?.id);
                    if (vendorAssociation) {
                        setReqVendorId(vendorAssociation.id);
                    }

                    const mappedItems = (data.enhancedItems?.list || []).map(item => {
                        const preRate = item.lastPurchaseRate;
                        const hasPreRate = preRate !== undefined && preRate !== null && preRate !== "" && Number(preRate) !== 0;
                        const rateValue = hasPreRate ? String(preRate) : '';
                        const qtyValue = parseFloat(item.qty) || 0;

                        return {
                            id: item.id,
                            productId: item.productId,
                            itemName: item.Product?.productName || item.productDescription,
                            description: item.Product?.description || item.productDescription,
                            size: item.size,
                            orderQty: qtyValue,
                            Unit: item.Unit,
                            rate: rateValue,
                            total: hasPreRate ? qtyValue * parseFloat(preRate) : 0,
                            readOnly: hasPreRate
                        };
                    });

                    setItems(mappedItems);
                } else {
                    setError(response.data.message || "Failed to load portal data.");
                }
            } catch (err) {
                console.error("Portal Data Fetch Error:", err);
                setError("An error occurred while loading the portal. The link might be expired.");
            } finally {
                setLoading(false);
            }
        };

        fetchPortalData();
    }, [requestId, vendorId]);

    const handleRateChange = (id, newRate) => {
        // Keep as string to allow empty input and free typing
        setItems(prevItems => prevItems.map(item => {
            if (item.id === id) {
                const numericRate = parseFloat(newRate) || 0;
                return { 
                    ...item, 
                    rate: newRate, 
                    total: item.orderQty * numericRate 
                };
            }
            return item;
        }));
    };

    useEffect(() => {
        const total = items.reduce((sum, item) => sum + item.total, 0);
        setGrandTotal(total);
    }, [items]);

    const handleSubmit = async () => {
        if (!reqVendorId) {
            Swal.fire('Error', 'Vendor association not found. Please contact support.', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Updated Quote?',
            text: 'Your updated rates will be sent back for approval.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Submit',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setSubmitting(true);

                const payload = {
                    reqVendorId: reqVendorId,
                    reqItemId: items.map(item => item.id),
                    rate: items.map(item => Number(item.rate) || 0)
                };

                const response = await axios.post(UPDATE_REQUEST_RATE_API, payload);

                if (response.data.status) {
                    setSubmitted(true);
                    Swal.fire({
                        icon: 'success',
                        title: 'Quote Submitted!',
                        text: 'Your quotation has been successfully updated.',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    Swal.fire('Failed', response.data.message || 'Failed to submit quote.', 'error');
                }
            } catch (error) {
                console.error('Quote Submission Error:', error);
                Swal.fire('Error', error.response?.data?.message || 'An error occurred while submitting your quote.', 'error');
            } finally {
                setSubmitting(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="vp-loader-container">
                <div className="vp-loader"></div>
                <p>Loading details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="vp-error-wrapper">
                <div className="vp-error-card">
                    <div className="vp-error-icon">
                        <MdError size={64} />
                    </div>
                    <h1>Access Denied</h1>
                    <p className="vp-error-msg">{error}</p>
                    <p className="vp-error-hint">
                        The secure link you are using may be expired, invalid, or tampered with.
                        Please request a new link from your contact person.
                    </p>
                    <div className="vp-error-actions">
                        <button onClick={() => window.location.reload()} className="vp-retry-btn">
                            Retry Connection
                        </button>
                        <a href="office@incepptgroup.company" target="_blank" rel="noopener noreferrer" className="vp-help-link">
                            Contact Support
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    if (!poData) {
        return <NotFound />;
    }

    const EMAIL_ADDRESS = "office@incepptgroup.company"

    if (submitted) {
        return (
            <div className="vp-success-container">
                <div className="vp-success-card">
                    <MdCheckCircle size={80} color="#10b981" />
                    <h1>Submission Successful</h1>
                    <p>
                        Thank you for your update. Our team will review it shortly.
                        For any queries, please contact <a href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</a>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="vendor-portal-wrapper">
            <div className="vp-container">
                <header className="vp-header">
                    <div className="vp-logo">
                        <MdCloudUpload size={32} />
                        <span>Inceppt Vendor Portal</span>
                    </div>
                    <div className="vp-po-badge">{poData.poNumber.toUpperCase() || poData.reqNumber.toUpperCase()}</div>
                </header>

                <main className="vp-content">
                    <section className="vp-info-section">
                        <div className="vp-info-grid">
                            <div className="vp-info-item">
                                <label>Vendor</label>
                                <span>{currentVendor?.vendorName || 'N/A'}</span>
                            </div>

                            <div className="vp-info-item">
                                <label>Date</label>
                                <span>{formatDate(poData.date)}</span>
                            </div>
                        </div>
                    </section>

                    <section className="vp-items-section">
                        {/* <div className="vp-section-header">
                            <h3>Item Details & Pricing</h3>
                            <input
                                type="text"
                                placeholder="Search items..."
                                className="vp-search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div> */}
                        {/* Desktop Table View */}
                        <div className="vp-table-container vp-desktop-only">
                            <table className="vp-items-table">
                                <thead>
                                    <tr>
                                        <th>Item Name & Description</th>
                                        <th>Size</th>
                                        <th>Order Qty</th>
                                        <th>Unit</th>
                                        <th>Your Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map(item => (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="vp-item-name">{item.itemName}</div>
                                                <div className="vp-item-meta">{item.description}</div>
                                            </td>
                                            <td>{item.size || "-"}</td>
                                            <td>{item.orderQty}</td>
                                            <td>{item.Unit?.unitName || "-"}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={item.rate}
                                                    onChange={(e) => handleRateChange(item.id, e.target.value)}
                                                    className="vp-rate-input"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    readOnly={item.readOnly}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Compact List View */}
                        <div className="vp-mobile-list vp-mobile-only">
                            {filteredItems.map(item => (
                                <div key={item.id} className="vp-mobile-item">
                                    <div className="vp-mobile-row-top">
                                        <span className="vp-mobile-name">{item.itemName}</span>
                                    </div>
                                    <div className="vp-mobile-row-desc">
                                        <p>{item.description}</p>
                                    </div>
                                    <div className="vp-mobile-row-btm">
                                        <div className="vp-mobile-specs">
                                            <span className="vp-mobile-size">Size: {item.size || "-"} | Qty: {item.orderQty} | Unit: {item.Unit?.unitName || "-"}</span>
                                        </div>
                                        <div className="vp-mobile-input-group">
                                            <span className="vp-currency-prefix">₹</span>
                                            <input
                                                type="number"
                                                value={item.rate}
                                                onChange={(e) => handleRateChange(item.id, e.target.value)}
                                                className="vp-mobile-rate-input"
                                                placeholder="Rate"
                                                readOnly={item.readOnly}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="vp-footer">
                        <div className="vp-summary">
                            <div className="vp-grand-total">
                                <label>Grand Total</label>
                                <span>{grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                        </div>
                        <button
                            className={`vp-submit-btn ${submitting ? 'loading' : ''}`}
                            onClick={handleSubmit}
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting...' : <><MdSend /> Submit Updated Quote</>}
                        </button>
                    </section>
                </main>

                <footer className="vp-site-footer">
                    <p>
                        &copy; {new Date().getFullYear()}{' '}
                        <a
                            href="https://www.incepptgroup.company/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Inceppt Group
                        </a>
                        . All rights reserved.
                    </p>

                    <p>Internal Secure Portal for Vendors</p>
                </footer>
            </div>
        </div>
    );
};

export default VendorPortal;
